/**
 * Utilitários de data usando date-fns para substituir moment.js
 * Este arquivo fornece funções compatíveis com a API do moment para facilitar a migração
 */

import {
  format,
  parse,
  parseISO,
  isValid,
  differenceInYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  differenceInMilliseconds,
  formatRelative,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  addDays,
  addMonths,
  addYears,
  addHours,
  subDays,
  subMonths,
  subYears,
  isBefore,
  isAfter,
  isSameDay,
  isSameMonth,
  isSameYear,
  getDay,
  getDate,
  getMonth,
  getYear,
  setMonth,
  setYear,
  setHours,
  setMinutes,
  getHours,
  getMinutes,
  eachDayOfInterval,
  eachMonthOfInterval,
} from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Formata uma data usando date-fns
 * Compatível com moment().format()
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  formatStr: string = "dd/MM/yyyy"
): string {
  if (!date) return "";

  try {
    const dateObj = typeof date === "string" || typeof date === "number" 
      ? new Date(date) 
      : date;

    if (!isValid(dateObj)) {
      return "";
    }

    // Formatos especiais do moment
    if (formatStr === "LL") {
      // "LL" = "MMMM D, YYYY" em moment, mas em português seria "D de MMMM de YYYY"
      return format(dateObj, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
    }
    if (formatStr === "LLL") {
      // "LLL" = "MMMM D, YYYY h:mm A" em moment
      return format(dateObj, "d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
    }

    // Converter formatos do moment para date-fns
    const dateFnsFormat = formatStr
      .replace(/DD/g, "dd")
      .replace(/D/g, "d")
      .replace(/MM/g, "MM")
      .replace(/M/g, "M")
      .replace(/YYYY/g, "yyyy")
      .replace(/YY/g, "yy")
      .replace(/HH/g, "HH")
      .replace(/H/g, "H")
      .replace(/mm/g, "mm")
      .replace(/m/g, "m")
      .replace(/ss/g, "ss")
      .replace(/s/g, "s");

    return format(dateObj, dateFnsFormat, { locale: ptBR });
  } catch (error) {
    console.error("Erro ao formatar data:", error);
    return "";
  }
}

/**
 * Parse de data usando date-fns
 * Compatível com moment(string, format)
 */
export function parseDate(
  dateString: string,
  formatStr: string = "dd/MM/yyyy"
): Date | null {
  if (!dateString) return null;

  try {
    // Converter formatos do moment para date-fns
    const dateFnsFormat = formatStr
      .replace(/DD/g, "dd")
      .replace(/D/g, "d")
      .replace(/MM/g, "MM")
      .replace(/M/g, "M")
      .replace(/YYYY/g, "yyyy")
      .replace(/YY/g, "yy");

    const parsed = parse(dateString, dateFnsFormat, new Date(), { locale: ptBR });
    
    return isValid(parsed) ? parsed : null;
  } catch (error) {
    console.error("Erro ao fazer parse da data:", error);
    return null;
  }
}

/**
 * Calcula a idade a partir de uma data de nascimento
 * Compatível com moment().diff(moment(date), "years")
 */
export function getAge(birthDate: Date | string | null | undefined): number | "" {
  if (!birthDate) return "";

  try {
    const dateObj = typeof birthDate === "string" 
      ? new Date(birthDate) 
      : birthDate;

    if (!isValid(dateObj)) {
      return "";
    }

    const age = differenceInYears(new Date(), dateObj);
    return isNaN(age) ? "" : age;
  } catch (error) {
    console.error("Erro ao calcular idade:", error);
    return "";
  }
}

/**
 * Verifica se uma data é válida
 */
export function isValidDate(date: Date | string | number | null | undefined): boolean {
  if (!date) return false;
  
  const dateObj = typeof date === "string" || typeof date === "number"
    ? new Date(date)
    : date;
    
  return isValid(dateObj);
}

/**
 * Obtém a data atual
 * Compatível com moment()
 */
export function now(): Date {
  return new Date();
}

/**
 * Cria um objeto de data a partir de uma string/número
 * Compatível com moment(date)
 */
export function createDate(
  date?: Date | string | number | null
): Date | null {
  if (!date) return new Date();

  try {
    const dateObj = typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

    return isValid(dateObj) ? dateObj : null;
  } catch (error) {
    console.error("Erro ao criar data:", error);
    return null;
  }
}

/**
 * Formata data para exibição em português
 */
export function formatDateBR(
  date: Date | string | number | null | undefined,
  formatStr: string = "dd/MM/yyyy"
): string {
  return formatDate(date, formatStr);
}

/**
 * Formata data e hora para exibição em português
 */
export function formatDateTimeBR(
  date: Date | string | number | null | undefined
): string {
  return formatDate(date, "dd/MM/yyyy HH:mm");
}

/**
 * Formata apenas a hora
 */
export function formatTimeBR(
  date: Date | string | number | null | undefined
): string {
  return formatDate(date, "HH:mm");
}

/**
 * Adiciona dias a uma data
 */
export function addDaysToDate(
  date: Date | string | number,
  days: number
): Date {
  const dateObj = typeof date === "string" || typeof date === "number"
    ? new Date(date)
    : date;
  return addDays(dateObj, days);
}

/**
 * Subtrai dias de uma data
 */
export function subtractDaysFromDate(
  date: Date | string | number,
  days: number
): Date {
  const dateObj = typeof date === "string" || typeof date === "number"
    ? new Date(date)
    : date;
  return subDays(dateObj, days);
}

/**
 * Verifica se uma data está antes de outra
 */
export function isDateBefore(
  date1: Date | string | number,
  date2: Date | string | number
): boolean {
  const d1 = typeof date1 === "string" || typeof date1 === "number"
    ? new Date(date1)
    : date1;
  const d2 = typeof date2 === "string" || typeof date2 === "number"
    ? new Date(date2)
    : date2;
  return isBefore(d1, d2);
}

/**
 * Verifica se uma data está depois de outra
 */
export function isDateAfter(
  date1: Date | string | number,
  date2: Date | string | number
): boolean {
  const d1 = typeof date1 === "string" || typeof date1 === "number"
    ? new Date(date1)
    : date1;
  const d2 = typeof date2 === "string" || typeof date2 === "number"
    ? new Date(date2)
    : date2;
  return isAfter(d1, d2);
}

/**
 * Verifica se duas datas são do mesmo dia
 */
export function isSameDate(
  date1: Date | string | number,
  date2: Date | string | number
): boolean {
  const d1 = typeof date1 === "string" || typeof date1 === "number"
    ? new Date(date1)
    : date1;
  const d2 = typeof date2 === "string" || typeof date2 === "number"
    ? new Date(date2)
    : date2;
  return isSameDay(d1, d2);
}

/**
 * Formata uma data no formato ICS (iCalendar)
 * Compatível com moment().utc().format('YYYYMMDD[T]HHmmss[Z]')
 */
export function formatICSDate(date: Date | string | number): string {
  const dateObj = typeof date === "string" || typeof date === "number"
    ? new Date(date)
    : date;

  if (!isValid(dateObj)) {
    return "";
  }

  // Converter para UTC
  const year = dateObj.getUTCFullYear();
  const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getUTCDate()).padStart(2, "0");
  const hours = String(dateObj.getUTCHours()).padStart(2, "0");
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getUTCSeconds()).padStart(2, "0");

  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Parse seguro de datas ISO do backend, ajustando timezone para datas puras (sem hora)
 * Útil para evitar que datas como "2024-01-15T00:00:00.000Z" apareçam como "14/01/2024" no Brasil
 */
export function parseISOSafe(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  
  try {
    // Se for uma string no formato YYYY-MM-DD (sem hora), criar data local
    if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-").map(Number);
      return new Date(year, month - 1, day);
    }

    const parsed = parseISO(dateString);
    if (!isValid(parsed)) return null;
    
    // Se a data vier com timezone UTC e for meia-noite (00:00:00), 
    // é provável que seja uma data pura (sem hora) e devemos ajustar
    const isUtcMidnight = 
      dateString.includes("T00:00:00") && 
      (dateString.endsWith("Z") || dateString.includes("+00:00"));

    const isBrtMidnightUtc =
      dateString.includes("T03:00:00") &&
      (dateString.endsWith("Z") || dateString.includes("+00:00"));
    
    if (isUtcMidnight || isBrtMidnightUtc) {
      const year = parsed.getUTCFullYear();
      const month = parsed.getUTCMonth();
      const day = parsed.getUTCDate();
      return new Date(year, month, day);
    }
    
    return parsed;
  } catch (error) {
    console.error("[DATE_UTILS] Erro ao fazer parse da data:", error);
    return null;
  }
}

// Re-exportar funções úteis do date-fns
export {
  format,
  parse,
  parseISO,
  isValid,
  differenceInYears,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  differenceInMilliseconds,
  formatRelative,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  addDays,
  addMonths,
  addYears,
  addHours,
  subDays,
  subMonths,
  subYears,
  isBefore,
  isAfter,
  isSameDay,
  isSameMonth,
  isSameYear,
  getDay,
  getDate,
  getMonth,
  getYear,
  setMonth,
  setYear,
  setHours,
  setMinutes,
  getHours,
  getMinutes,
  eachDayOfInterval,
  eachMonthOfInterval,
  ptBR,
};
